import { supabase } from './supabase';
import { apiKeyService } from './apiKeyService';
import { SecurityService } from './securityService';
import { DataForSEOSettings } from './types';

export interface InstantPageResult {
  id: string;
  status_code: number;
  status_message: string;
  time: string;
  cost: number;
  result_count: number;
  data: {
    url: string;
    enable_javascript: boolean;
    load_resources: boolean;
    enable_browser_rendering: boolean;
    disable_cookie_popup: boolean;
    check_spell: boolean;
  };
  result: InstantPageItem[];
}

export interface InstantPageItem {
  crawl_progress: string;
  crawl_status: string | null;
  crawl_gateway_address: string;
  items_count: number;
  items: PageItem[];
}

export interface ResourceError {
  line: number;
  column: number;
  message: string;
  status_code: number;
}

export interface ResourceErrors {
  errors: ResourceError[];
  warnings: ResourceError[];
}

export interface CacheControl {
  cachable: boolean;
  ttl: number;
}

export interface PageItem {
  resource_type: string;
  status_code: number;
  location: string | null;
  url: string;
  meta: PageMeta;
  page_timing: PageTiming;
  onpage_score: number;
  total_dom_size: number;
  custom_js_response: any;
  custom_js_client_exception: any;
  resource_errors: ResourceErrors;
  broken_resources: boolean;
  broken_links: boolean;
  duplicate_title: boolean;
  duplicate_description: boolean;
  duplicate_content: boolean;
  click_depth: number;
  size: number;
  encoded_size: number;
  total_transfer_size: number;
  fetch_time: string;
  cache_control: CacheControl;
  checks: PageChecks;
  content_encoding: string;
  media_type: string;
  server: string;
  is_resource: boolean;
  url_length: number;
  relative_url_length: number;
  last_modified: {
    header: string;
    sitemap: string | null;
    meta_tag: string;
  };
}

export interface PageMeta {
  title: string;
  charset: number;
  follow: boolean;
  generator?: string;
  htags: {
    h1?: string[];
    h2?: string[];
    h3?: string[];
    h4?: string[];
    h5?: string[];
    h6?: string[];
  };
  description: string;
  favicon?: string;
  meta_keywords: string | null;
  canonical: string;
  internal_links_count: number;
  external_links_count: number;
  inbound_links_count: number;
  images_count: number;
  images_size: number;
  scripts_count: number;
  scripts_size: number;
  stylesheets_count: number;
  stylesheets_size: number;
  title_length: number;
  description_length: number;
  render_blocking_scripts_count: number;
  render_blocking_stylesheets_count: number;
  cumulative_layout_shift: number;
  meta_title: string | null;
  content: {
    plain_text_size: number;
    plain_text_rate: number;
    plain_text_word_count: number;
    automated_readability_index: number;
    coleman_liau_readability_index: number;
    dale_chall_readability_index: number;
    flesch_kincaid_readability_index: number;
    smog_readability_index: number;
    description_to_content_consistency: number;
    title_to_content_consistency: number;
    meta_keywords_to_content_consistency: number | null;
  };
  deprecated_tags: any;
  duplicate_meta_tags: any;
  spell: any;
  social_media_tags: Record<string, string>;
}

export interface PageTiming {
  time_to_interactive: number;
  dom_complete: number;
  largest_contentful_paint: number;
  first_input_delay: number;
  connection_time: number;
  waiting_time: number;
  download_time: number;
  duration_time: number;
}

export interface PageChecks {
  no_content_encoding: boolean;
  high_loading_time: boolean;
  is_redirect: boolean;
  is_4xx_code: boolean;
  is_5xx_code: boolean;
  is_broken: boolean;
  is_www: boolean;
  is_https: boolean;
  is_http: boolean;
  high_waiting_time: boolean;
  has_micromarkup: boolean;
  has_micromarkup_errors: boolean;
  no_doctype: boolean;
  has_html_doctype: boolean;
  canonical: boolean;
  no_encoding_meta_tag: boolean;
  no_h1_tag: boolean;
  https_to_http_links: boolean;
  size_greater_than_3mb: boolean;
  meta_charset_consistency: boolean;
  has_meta_refresh_redirect: boolean;
  has_render_blocking_resources: boolean;
  low_content_rate: boolean;
  high_content_rate: boolean;
  low_character_count: boolean;
  high_character_count: boolean;
  small_page_size: boolean;
  large_page_size: boolean;
  low_readability_rate: boolean;
  irrelevant_description: boolean;
  irrelevant_title: boolean;
  irrelevant_meta_keywords: boolean;
  title_too_long: boolean;
  has_meta_title: boolean;
  title_too_short: boolean;
  deprecated_html_tags: boolean;
  duplicate_meta_tags: boolean;
  duplicate_title_tag: boolean;
  no_image_alt: boolean;
  no_image_title: boolean;
  no_description: boolean;
  no_title: boolean;
  no_favicon: boolean;
  seo_friendly_url: boolean;
  flash: boolean;
  frame: boolean;
  lorem_ipsum: boolean;
  seo_friendly_url_characters_check: boolean;
  seo_friendly_url_dynamic_check: boolean;
  seo_friendly_url_keywords_check: boolean;
  seo_friendly_url_relative_length_check: boolean;
  [key: string]: boolean;
}

export interface InstantPageAnalysisResult {
  prospect_id: string;
  url: string;
  analysis_date: string;
  onpage_score: number;
  page_timing: PageTiming;
  meta: PageMeta;
  resource_errors: ResourceErrors;
  cache_control: CacheControl;
  checks: PageChecks;
  technical_info: {
    total_dom_size: number;
    size: number;
    encoded_size: number;
    total_transfer_size: number;
    content_encoding: string;
    media_type: string;
    server: string;
    url_length: number;
    relative_url_length: number;
    broken_resources: boolean;
    broken_links: boolean;
    duplicate_title: boolean;
    duplicate_description: boolean;
    duplicate_content: boolean;
  };
  seo_issues: {
    critical: number;
    warnings: number;
    notices: number;
  };
  raw_response: InstantPageResult;
}

export class InstantPageService {
  // 🛡️ Protection contre les appels multiples simultanés
  private activeAnalyses: Set<string> = new Set();
  
  /**
   * Analyse une URL avec DataForSEO Instant Pages API
   */
  async analyzeUrl(url: string, prospectId?: string, forceRefresh: boolean = false): Promise<InstantPageAnalysisResult | null> {
  try {
    
    // 🛡️ Créer une clé unique pour cette analyse
    const analysisKey = `${prospectId || 'no-prospect'}_${url}`;
    
    // 🛡️ Vérifier si une analyse est déjà en cours pour cette URL/prospect
    if (this.activeAnalyses.has(analysisKey)) {
      // Attendre un peu et réessayer (maximum 30 secondes)
      let attempts = 0;
      while (this.activeAnalyses.has(analysisKey) && attempts < 30) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
      
      if (this.activeAnalyses.has(analysisKey)) {
        return null;
      }
    }
    
    // 🛡️ Marquer cette analyse comme active
    this.activeAnalyses.add(analysisKey);
    
    try {
      // Si un ID prospect est fourni, vérifier si des données existent déjà
      if (prospectId && !forceRefresh) {
        const { data: storedData, error } = await supabase
          .from('instant_page_analysis')
          .select('*')
          .eq('prospect_id', prospectId)
          .eq('url', url)
          .maybeSingle();
          
        if (!error && storedData) {
          return storedData as InstantPageAnalysisResult;
        }
      }
      
      // Récupérer les données depuis DataForSEO
      const analysisResult = await this.fetchInstantPageAnalysis(url);
      
      if (analysisResult) {
        
        // Si un ID prospect est fourni, stocker les résultats
        if (prospectId) {
          await this.saveAnalysisResult(analysisResult, prospectId);
        }
        
        return analysisResult;
      }
      
      return null;
    } finally {
      // 🛡️ Retirer cette analyse de la liste des analyses actives
      this.activeAnalyses.delete(analysisKey);
    }
  } catch (error) {
    console.error('❌ Erreur lors de l\'analyse de l\'URL:', error);
    // 🛡️ S'assurer de nettoyer en cas d'erreur
    const analysisKey = `${prospectId || 'no-prospect'}_${url}`;
    this.activeAnalyses.delete(analysisKey);
    return null;
  }
}
  
  /**
   * Récupère l'analyse de page instantanée depuis DataForSEO
   */
  async fetchInstantPageAnalysis(url: string): Promise<InstantPageAnalysisResult | null> {
    try {
      const settings = await this.getSettings();
      
      if (!settings || !settings.login || !settings.api_key) {
        console.error('Identifiants DataForSEO manquants');
        return null;
      }
      
      // Utiliser btoa() au lieu de Buffer pour l'encodage base64 (compatible avec les navigateurs)
      const auth = btoa(`${settings.login}:${settings.api_key}`);
      
      // S'assurer que l'URL a un protocole (https:// ou http://)
      let fullUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        fullUrl = `https://${url}`;
      }
      
      const requestData = {
        url: fullUrl,
        enable_javascript: true,
        load_resources: true,
        enable_browser_rendering: true
      };
      
      const response = await fetch('https://api.dataforseo.com/v3/on_page/instant_pages', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify([requestData])
      });
      
      const result = await response.json();
      
      if (!result || !result.tasks || result.tasks.length === 0) {
        console.error('Aucun résultat dans la réponse DataForSEO');
        return null;
      }
      
      const taskResult = result.tasks[0];
      
      if (taskResult.status_code !== 20000) {
        console.error('Erreur DataForSEO:', taskResult.status_message);
        return null;
      }
      
      // Si les résultats ne sont pas encore prêts
      if (!taskResult.result || taskResult.result.length === 0 || !taskResult.result[0].items || taskResult.result[0].items.length === 0) {
        console.error('Résultats d\'analyse non disponibles');
        return null;
      }
      
      const pageData = taskResult.result[0].items[0];
      
      // Vérifier si la ressource est "broken" (erreur 4xx/5xx)
      if (pageData.resource_type === 'broken' || pageData.checks?.is_broken) {
        return {
          prospect_id: '',
          url: fullUrl,
          analysis_date: new Date().toISOString(),
          onpage_score: 0,
          page_timing: null,
          meta: null,
          resource_errors: null,
          cache_control: null,
          checks: pageData.checks || null,
          technical_info: {
            total_dom_size: 0,
            size: pageData.size || 0,
            encoded_size: pageData.encoded_size || 0,
            total_transfer_size: pageData.total_transfer_size || 0,
            content_encoding: pageData.content_encoding || null,
            media_type: pageData.media_type || null,
            server: pageData.server || null,
            url_length: 0,
            relative_url_length: 0,
            broken_resources: true,
            broken_links: true,
            duplicate_title: false,
            duplicate_description: false,
            duplicate_content: false
          },
          seo_issues: {
            critical: 1,
            warnings: 0,
            notices: 0
          },
          raw_response: taskResult,
          is_broken: true,
          status_code: pageData.status_code,
          error_message: this.getErrorMessage(pageData.status_code)
        } as any;
      }
      
      // Calculer le nombre de problèmes SEO
      const seoIssues = this.calculateSeoIssues(pageData.checks);
      
      // Formater les résultats avec toutes les données enrichies
      const analysisResult: InstantPageAnalysisResult = {
        prospect_id: '',
        url: fullUrl, // Utiliser l'URL complète avec protocole
        analysis_date: new Date().toISOString(),
        onpage_score: pageData.onpage_score,
        page_timing: pageData.page_timing,
        meta: pageData.meta,
        resource_errors: pageData.resource_errors,
        cache_control: pageData.cache_control,
        checks: pageData.checks,
        technical_info: {
          total_dom_size: pageData.total_dom_size,
          size: pageData.size,
          encoded_size: pageData.encoded_size,
          total_transfer_size: pageData.total_transfer_size,
          content_encoding: pageData.content_encoding,
          media_type: pageData.media_type,
          server: pageData.server,
          url_length: pageData.url_length,
          relative_url_length: pageData.relative_url_length,
          broken_resources: pageData.broken_resources,
          broken_links: pageData.broken_links,
          duplicate_title: pageData.duplicate_title,
          duplicate_description: pageData.duplicate_description,
          duplicate_content: pageData.duplicate_content
        },
        seo_issues: seoIssues,
        raw_response: taskResult
      };
      
      return analysisResult;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'analyse de page:', error);
      return null;
    }
  }
  
  /**
   * Calcule le nombre de problèmes SEO à partir des vérifications
   */
  calculateSeoIssues(checks: PageChecks): { critical: number; warnings: number; notices: number } {
    const critical = [
      'is_broken', 'is_4xx_code', 'is_5xx_code', 'no_title', 'no_h1_tag', 
      'is_http', 'irrelevant_title', 'irrelevant_description'
    ];
    
    const warnings = [
      'high_loading_time', 'no_description', 'no_favicon', 'no_image_alt', 
      'duplicate_title_tag', 'duplicate_meta_tags', 'low_content_rate', 'high_loading_time'
    ];
    
    const notices = [
      'no_content_encoding', 'has_render_blocking_resources', 'no_meta_title', 
      'title_too_long', 'title_too_short', 'seo_friendly_url_characters_check'
    ];
    
    let criticalCount = 0;
    let warningCount = 0;
    let noticeCount = 0;
    
    for (const key in checks) {
      if (critical.includes(key) && checks[key]) criticalCount++;
      if (warnings.includes(key) && checks[key]) warningCount++;
      if (notices.includes(key) && checks[key]) noticeCount++;
    }
    
    return {
      critical: criticalCount,
      warnings: warningCount,
      notices: noticeCount
    };
  }
  
  /**
   * Enregistre les résultats d'analyse dans la base de données
   */
  async saveAnalysisResult(result: InstantPageAnalysisResult, prospectId: string): Promise<void> {
    try {
      // Préparer les données en s'assurant que tous les objets JSON sont correctement sérialisés
      const analysisToSave = {
        prospect_id: prospectId,
        url: result.url,
        analysis_date: result.analysis_date,
        onpage_score: result.onpage_score,
        page_timing: result.page_timing ? JSON.parse(JSON.stringify(result.page_timing)) : null,
        seo_issues: result.seo_issues ? JSON.parse(JSON.stringify(result.seo_issues)) : null,
        raw_response: result.raw_response ? JSON.parse(JSON.stringify(result.raw_response)) : null,
        // Nouvelles colonnes ajoutées
        meta: result.meta ? JSON.parse(JSON.stringify(result.meta)) : null,
        technical_info: result.technical_info ? JSON.parse(JSON.stringify(result.technical_info)) : null,
        resource_errors: result.resource_errors ? JSON.parse(JSON.stringify(result.resource_errors)) : null,
        checks: result.checks ? JSON.parse(JSON.stringify(result.checks)) : null,
        content_encoding: result.technical_info?.content_encoding || null,
        media_type: result.technical_info?.media_type || null,
        server: result.technical_info?.server || null,
        total_dom_size: result.technical_info?.total_dom_size || null,
        size: result.technical_info?.size || null,
        encoded_size: result.technical_info?.encoded_size || null,
        total_transfer_size: result.technical_info?.total_transfer_size || null,
        url_length: result.technical_info?.url_length || null,
        relative_url_length: result.technical_info?.relative_url_length || null,
        broken_resources: result.technical_info?.broken_resources || false,
        broken_links: result.technical_info?.broken_links || false,
        duplicate_title: result.technical_info?.duplicate_title || false,
        duplicate_description: result.technical_info?.duplicate_description || false,
        duplicate_content: result.technical_info?.duplicate_content || false,
        click_depth: result.raw_response?.result?.[0]?.items?.[0]?.click_depth || null,
        fetch_time: result.raw_response?.result?.[0]?.items?.[0]?.fetch_time || null,
        cache_control: result.cache_control ? JSON.parse(JSON.stringify(result.cache_control)) : null,
        last_modified: result.raw_response?.result?.[0]?.items?.[0]?.last_modified ? 
          JSON.parse(JSON.stringify(result.raw_response.result[0].items[0].last_modified)) : null,
        social_media_tags: result.meta?.social_media_tags ? 
          JSON.parse(JSON.stringify(result.meta.social_media_tags)) : null
      };
      
      
      const { error } = await supabase
        .from('instant_page_analysis')
        .upsert(analysisToSave, { 
          onConflict: 'prospect_id,url',
          ignoreDuplicates: false
        });
      
      if (error) {
        console.error('Erreur lors de l\'enregistrement des résultats d\'analyse:', error);
        throw error;
      } else {
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des résultats d\'analyse:', error);
      throw error;
    }
  }
  
  /**
   * Retourne un message d'erreur basé sur le code de statut HTTP
   */
  getErrorMessage(statusCode: number): string {
    switch (statusCode) {
      case 403:
        return "Accès interdit - Le site bloque l'accès à cette page";
      case 404:
        return "Page non trouvée - Cette URL n'existe pas";
      case 500:
        return "Erreur serveur - Le serveur du site rencontre des problèmes";
      case 502:
        return "Passerelle défaillante - Problème de connexion au serveur";
      case 503:
        return "Service indisponible - Le site est temporairement hors service";
      case 504:
        return "Délai d'attente dépassé - Le serveur met trop de temps à répondre";
      default:
        return `Erreur HTTP ${statusCode} - Impossible d'accéder à cette page`;
    }
  }

  /**
   * Récupère les identifiants DataForSEO depuis la table api_keys
   */
  async getSettings(): Promise<DataForSEOSettings | null> {
    try {
      const config = await apiKeyService.getApiConfig('dataforseo');
      
      if (config) {
        const decryptedKey = config.api_key ? await SecurityService.decryptApiKey(config.api_key) : '';
        
        return {
          login: config.metadata?.login || '',
          api_key: decryptedKey
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des identifiants DataForSEO:', error);
      return null;
    }
  }
  
  /**
   * Récupère les analyses de page pour un prospect
   */
  async getAnalysesForProspect(prospectId: string): Promise<InstantPageAnalysisResult[]> {
    try {
      const { data, error } = await supabase
        .from('instant_page_analysis')
        .select('*')
        .eq('prospect_id', prospectId);
      
      if (error) {
        console.error('Erreur lors de la récupération des analyses:', error);
        return [];
      }
      
      return data as InstantPageAnalysisResult[];
    } catch (error) {
      console.error('Erreur lors de la récupération des analyses:', error);
      return [];
    }
  }
}

export const instantPageService = new InstantPageService();
